/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import { RootState } from '../../../../redux/store';
import {
  openModal,
  setModalType,
  setModalData
} from '../../../../redux/actions';

import { toTitleCase, formatRoundingType } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Facility, UserList } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';

interface UsersTableProps {
  facilities?: Facility[];
  isLoading?: boolean;
  totalCount: number;
  list: UserList[];
}

type NumberBooleanMap = {
  [key: number]: boolean;
};

const UsersTable: FC<UsersTableProps> = ({
  facilities,
  isLoading,
  totalCount = 0,
  list = []
}) => {
  const dispatch = useDispatch();

  const [listAllAccessPlaces, setListAllAccessPlaces] =
    useState<NumberBooleanMap>({});
  const [sortKey, setSortKey] = useState<string>('fullName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Employee');

  const getAccessPlacesById = (id: string) => {
    const obj = facilities?.find(
      (facility: Facility) => facility.facilityId === id
    );
    return obj ? obj.facility : '';
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case '3/4 ID':
        key = 'hcaid';
        break;
      case 'User Role':
        key = 'roles';
        break;
      case 'Position':
        key = 'title';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openUserForEdit = (user: UserList) => {
    dispatch(setModalData(user));
    dispatch(setModalType('Edit User'));
    dispatch(openModal());
  };

  const showAllAccessPlaces = (i: number) => {
    const obj = {
      [i]: true
    };
    setListAllAccessPlaces(obj);
  };

  const sortedUsers = useMemo(() => {
    const stateCopy = [...list];

    stateCopy.sort((a: UserList, b: UserList) =>
      a[sortKey] > b[sortKey]
        ? sortDir === 'asc'
          ? 1
          : -1
        : sortDir === 'asc'
        ? -1
        : 1
    );

    return stateCopy;
  }, [list, sortKey, sortDir]);

  const isTableEmpty = useMemo(() => sortedUsers.length === 0, [sortedUsers]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="User-Table-Employee-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="3/4 ID"
      id="User-Table-34-Column"
      unit="column"
      colWidth={10}
      direction={!(active === '3/4 ID') ? 'none' : sortDir}
      sort
    >
      3/4 ID
    </CellContainer>,
    <CellContainer
      key="User Role"
      id="User-Table-Role-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'User Role') ? 'none' : sortDir}
      sort
    >
      User Role
    </CellContainer>,
    <CellContainer
      key="Access Level"
      id="User-Table-Access-Level-Column"
      unit="column"
      colWidth={10}
      direction="none"
    >
      Access Level
    </CellContainer>,
    <CellContainer
      key="Access Places"
      id="User-Table-Access-Places-Column"
      unit="column"
      colWidth={20}
      direction="none"
    >
      Access Places
    </CellContainer>,
    <CellContainer
      key="Position"
      id="User-Table-Position-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Position') ? 'none' : sortDir}
      sort
    >
      Position
    </CellContainer>
  ];

  const rows = sortedUsers?.map((user: UserList, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`User-Row-${i}`}
        id={`User-Row-${user.hcaid}`}
        unit="row"
        size="default"
        height="5em"
        role="button"
        onClick={() => openUserForEdit(user)}
      >
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel>
            {toTitleCase(`${user.firstName} ${user.lastName}`)}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>{user.hcaid.toUpperCase()}</StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {user.roles.map(
              (role: string, index: number) =>
                `${formatRoundingType(role)}${
                  index + 1 < user.roles.length ? ', ' : ''
                }`
            )}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={10}>
          <StyledLabel>
            {user.access &&
              (toTitleCase(user.access.accessLevel) === 'Master'
                ? 'Corporate'
                : toTitleCase(user.access.accessLevel))}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {user.access && (
              <>
                {user.access.accessLevel === 'facility'
                  ? user.access.accessLevelIds.map((id: string, j: number) => {
                      const facility = getAccessPlacesById(id);
                      if (j <= 2)
                        return `${facility}${
                          j === user.access.accessLevelIds.length - 1
                            ? ' '
                            : ', '
                        }`;
                      if (j >= 2 && listAllAccessPlaces[i]) {
                        return `${facility}${
                          j === user.access.accessLevelIds.length - 1
                            ? ' '
                            : ', '
                        }`;
                      }
                      return false; // for consistent-return
                    })
                  : // if access place is master or division, just display the accessLevelIds
                    user.access.accessLevelIds.map((id: string, j: number) => {
                      const facility = id;
                      if (j <= 2)
                        return `${facility}${
                          j === user.access.accessLevelIds.length - 1
                            ? ' '
                            : ', '
                        }`;
                      if (j >= 2 && listAllAccessPlaces[i]) {
                        return `${facility}${
                          j === user.access.accessLevelIds.length - 1
                            ? ' '
                            : ', '
                        }`;
                      }
                      return false; // for consistent-return
                    })}

                {user.access.accessLevelIds?.length > 2 &&
                !listAllAccessPlaces[i] ? (
                  <span
                    className="show-more-text"
                    role="link"
                    tabIndex={0}
                    onClick={() => showAllAccessPlaces(i)}
                    onKeyDown={() => showAllAccessPlaces(i)}
                  >
                    Show more
                  </span>
                ) : null}
              </>
            )}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel style={{ whiteSpace: 'normal' }}>
            {user.title ? user.title : ''}
            {user.title && user.dept ? ', ' : ''}
            {user.dept ? user.dept : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="admin-areas-unit-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      loading={isLoading}
      totalResults={totalCount}
      type="users"
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    facilities: state.ConfigReducer.facilities
  };
};

export default connect(mapReduxStateToProps)(UsersTable);
